import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import RoleGuard from '../../components/Auth/RoleGuard';
import { authService } from '../../services/authService';
import { roleService } from '../../services/roleService';


const UserManagement = () => {
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    assignedDistrict: '',
    password: '',
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    searchTerm: '',
    searchBy: '',
    status: '',
    district: '',
    sortBy: '',
    sortDir: 'asc',
  });

  // local debounced input for search to avoid querying on every keystroke
  const [searchInput, setSearchInput] = useState(filters.searchTerm || '');

  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['users', page, rowsPerPage, filters],
    queryFn: () => {
      return authService.getUsers({
        page: page + 1,
        per_page: rowsPerPage,
        search_by: filters.searchBy,
        term: filters.searchTerm,
        status: filters.status,
        district: filters.district,
        sort_by: filters.sortBy,
        sort_dir: filters.sortDir,
      });
    },
    keepPreviousData: true
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => {
      return roleService.getRoles();
    },
    keepPreviousData: true
  });

  const roleOptions = roles.map(role => (
    { id: role.id, name: role.name, fullName: role.fullName }
  ));

  const users = data?.users || [];
  const totalCount = data?.total || 0;
  
  const createUserMutation = useMutation({
    mutationFn: (userData) => {
      // Mock API call
      console.log('Creating user:', userData);
      return Promise.resolve(userData);
    },
    onSuccess: () => {
      toast.success('User created successfully');
      setOpen(false);
      setUserForm({
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        assignedDistrict: '',
        password: '',
      });
      queryClient.invalidateQueries(['users']);
    },
    onError: () => {
      toast.error('Failed to create user');
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: (userData) => {
      console.log('Updating user:', userData);
      return Promise.resolve(userData);
    },
    onSuccess: () => {
      toast.success('User updated successfully');
      setOpen(false);
      setEditUser(null);
      queryClient.invalidateQueries(['users']);
    },
    onError: () => {
      toast.error('Failed to update user');
    },
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortChange = (key) => {
    setFilters(prev => {
      if (prev.sortBy === key) {
        return { ...prev, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' };
      }
      return { ...prev, sortBy: key, sortDir: 'asc' };
    });
    setPage(0);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== filters.searchTerm && searchInput.trim() !== '' && searchInput.length > 2) {
        handleFilterChange('searchTerm', searchInput);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleCreateUser = () => {
    setEditUser(null);
    setUserForm({
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      assignedDistrict: '',
      password: '',
    });
    setOpen(true);
  };

  const handleEditUser = (user) => {
    setEditUser(user);
    setUserForm({
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role.name,
      assignedDistrict: user.assignedDistrict || '',
      password: '',
    });
    setOpen(true);
  };

  const handleSubmit = () => {
    if (editUser) {
      updateUserMutation.mutate({ ...userForm, id: editUser.id });
    } else {
      createUserMutation.mutate(userForm);
    }
  };

  const getRoleColor = (roleName) => {
    const colors = {
      admin: 'error',
      l1_staff: 'primary',
      citizen: 'default',
      dc: 'secondary',
      dpo: 'info',
    };
    return colors[roleName] || 'default';
  };

  return (
    <RoleGuard requiredPermissions={['manage_users', 'view_users', 'admin_access']}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              User Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage system users, roles, and permissions
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Tooltip title="Refresh">
              <IconButton onClick={() => queryClient.invalidateQueries(['users'])}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateUser}
            >
              Add User
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by username, email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Search By"
                value={filters.searchBy}
                onChange={(e) => handleFilterChange('searchBy', e.target.value)}
              >
                <MenuItem value="">Auto</MenuItem>
                <MenuItem value="username">Username</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="first_name">First Name</MenuItem>
                <MenuItem value="last_name">Last Name</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                select
                label="District"
                value={filters.district}
                onChange={(e) => handleFilterChange('district', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GT">Gangtok</MenuItem>
                <MenuItem value="MN">Mangan</MenuItem>
                <MenuItem value="GY">Gyalshing</MenuItem>
                <MenuItem value="NM">Namchi</MenuItem>
                <MenuItem value="PK">Pakyong</MenuItem>
                <MenuItem value="SR">Soreng</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="outlined" onClick={() => { setFilters({ searchTerm: '', searchBy: '', status: '', district: '' }); setSearchInput(''); }}>Clear</Button>
            </Grid>
          </Grid>
        </Card>

        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={3}>
              <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                System Users ({totalCount})
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sortBy === 'username'}
                        direction={filters.sortDir}
                        onClick={() => handleSortChange('username')}
                      >
                        User
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sortBy === 'role'}
                        direction={filters.sortDir}
                        onClick={() => handleSortChange('role')}
                      >
                        Role
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>District</TableCell>
                    <TableCell>SD | Block</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sortBy === 'created_at'}
                        direction={filters.sortDir}
                        onClick={() => handleSortChange('created_at')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2 }}>
                            {user.first_name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {user.first_name} {user.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.username} • {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role.name}
                          color={getRoleColor(user.role.name)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.district !== null ? user.district.code : '-'}
                      </TableCell>
                      <TableCell>
                        {user.subdivision !== null ? user.subdivision.name : '-'} | {user.block !== null ? user.block.name : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Active' : 'Inactive'}
                          color={user.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Manage Permissions">
                          <IconButton size="small">
                            <SecurityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </CardContent>
        </Card>

        {/* Add/Edit User Dialog */}
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editUser ? 'Edit User' : 'Add New User'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={userForm.firstName}
                  onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={userForm.lastName}
                  onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    label="Role"
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.name}>
                        {role.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Assigned District</InputLabel>
                  <Select
                    value={userForm.assignedDistrict}
                    onChange={(e) => setUserForm({ ...userForm, assignedDistrict: e.target.value })}
                    label="Assigned District"
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="GT">Gangtok</MenuItem>
                    <MenuItem value="MN">Mangan</MenuItem>
                    <MenuItem value="GY">Gyalshing</MenuItem>
                    <MenuItem value="NM">Namchi</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {!editUser && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={createUserMutation.isLoading || updateUserMutation.isLoading}
            >
              {editUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </RoleGuard>
  );
};

export default UserManagement;
